import { useT } from "@transifex/react";
import type { GoodLuckSettings, SettingsSection, SettingsSubSection, TeacherTestDetails } from "p6m-p6u";
import InputWithActions from "../../../../../../basic/inputWithActions/InputWithActions";
import DesignConstants from "../../../../../../constants/DesignConstants";
import { GoodLuckNames, SettingPosition } from "../../../../../../enums/settings";
import { ChangeSettingsParams } from "../../../PDFConfiguration";
import PDFSettingsTopBottomToggle from "../../../pdfSettingsTopBottomButton/PDFSettingsTopBottomToggle";
import PDFSettingDetails from "../../pdfSettingDetails/PDFSettingDetails";
import GoodLuckImagesGrid from "./pdfGoodLuckImagesGrid/PDFGoodLuckImagesGrid";
import { useState } from "react";

import { PDFEditableTextWrapper, PDFGridWrapper, PDFSettingsTopBottomWrapper, PDFTextHeader } from "./StyledComponents";

interface PDFGoodLuckSettingControlProps {
    teacherTestDetails: TeacherTestDetails;
    changeSettings: <T extends SettingsSection, K extends SettingsSubSection<T>>(
        changeSettingsParams: ChangeSettingsParams<T, K>
    ) => void;
}

const PDFGoodLuckSettingControl = (props: PDFGoodLuckSettingControlProps) => {
    const t = useT();

    const t_goodLuck = t("Good Luck!", { _tags: "PDFConfiguration" });

    const text = props.teacherTestDetails?.settings?.additional?.goodLuck?.text;

    const [isEditing, setIsEditing] = useState(false);
    const [currentText, setCurrentText] = useState(text === "default_good_luck_text" ? t_goodLuck : text);

    const t_showImage = t("Show Image", { _tags: "PDFConfiguration" });
    const t_shownText = t("Shown Text:", { _tags: "PDFConfiguration" });
    const t_goodLuckText = t("Good luck text...", { _tags: "PDFConfiguration" });

    const goodLuckImageIsActive = !!props.teacherTestDetails?.settings?.additional?.goodLuck?.show;
    const imagePosition = props.teacherTestDetails?.settings?.additional?.goodLuck?.position;
    const goodLuckImageName = props.teacherTestDetails?.settings?.additional?.goodLuck?.value;

    const getOppositePosition = (currentPosition: SettingPosition) => {
        return currentPosition === SettingPosition.TOP ? SettingPosition.BOTTOM : SettingPosition.TOP;
    };

    const changeGoodLuckSetting = <T extends keyof GoodLuckSettings>(key: T, value: GoodLuckSettings[T]) => {
        props.changeSettings({
            sectionTitle: "additional",
            settingTitle: "goodLuck",
            setting: { key, value },
        });
    };

    const togglePosition = () => {
        changeGoodLuckSetting("position", getOppositePosition(imagePosition));
    };

    const toggleVisibility = () => {
        const opposite = !props.teacherTestDetails.settings?.additional.goodLuck.show;
        changeGoodLuckSetting("show", opposite);
    };

    const handleImageChange = (goodLuckImageName: GoodLuckNames) => {
        changeGoodLuckSetting("value", goodLuckImageName);
    };

    const changeGoodLuckTextValue = (value: string) => {
        changeGoodLuckSetting("text", value);
    };

    return (
        <PDFGridWrapper>
            <PDFSettingDetails
                checked={goodLuckImageIsActive}
                onChange={toggleVisibility}
                statusText={t_showImage}
            >
                <PDFEditableTextWrapper>
                    <PDFTextHeader>{t_shownText}</PDFTextHeader>
                    <InputWithActions
                        isEditing={isEditing}
                        setIsEditing={() => setIsEditing(true)}
                        placeholder={t_goodLuckText}
                        currentText={currentText}
                        onChange={(e) => setCurrentText(e.target.value)}
                        iconsize={DesignConstants.FONT_SIZES.XL}
                        fontSize={DesignConstants.FONT_SIZES.M}
                        underlined
                        disabled={!goodLuckImageIsActive}
                        onSave={() => {
                            if (currentText !== text) changeGoodLuckTextValue(currentText);
                            setIsEditing(false);
                        }}
                        onDiscard={() => {
                            setCurrentText(currentText ?? "");
                            setIsEditing(false);
                        }}
                        onDelete={() => {
                            setCurrentText("");
                            changeGoodLuckTextValue("");
                            setIsEditing(false);
                        }}
                    />
                </PDFEditableTextWrapper>
            </PDFSettingDetails>
            <GoodLuckImagesGrid
                selectedImgName={goodLuckImageName}
                onImgChange={handleImageChange}
                disabled={!goodLuckImageIsActive}
            />
            <PDFSettingsTopBottomWrapper>
                <PDFSettingsTopBottomToggle
                    isTop={imagePosition === SettingPosition.TOP}
                    togglePosition={togglePosition}
                    disabled={!goodLuckImageIsActive}
                />
            </PDFSettingsTopBottomWrapper>
        </PDFGridWrapper>
    );
};

export default PDFGoodLuckSettingControl;
